import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import GppGoodIcon from "@mui/icons-material/GppGoodOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha, keyframes } from "@mui/material/styles";
import { Brand } from "./Brand";
import { AnimatedTitle } from "./AnimatedTitle";
import { AdaltraTubes } from "./AdaltraTubes";
import { LanguageMenu } from "./LanguageMenu";
import { authFieldsSx } from "../theme/authFields";
import {
  ALTO_PANTALLA,
  marcaFija,
  motion,
  timing,
  variablesClaras,
} from "../theme/atlasAuthTheme";

/** Punto en el que la pantalla se divide en dos paneles animados. */
const SPLIT = "@media (min-width:621px)";

/**
 * Intercambio de los dos paneles.
 *
 * Es movimiento en pantalla —dos bloques que ya están ahí y cambian de
 * sitio—, así que acelera y frena como lo haría cualquier objeto real. Es la
 * única curva de este tipo del portal; todo lo que aparece o desaparece usa
 * la de entrada, que arranca rápido.
 *
 * Estaba en 0,72 s. Ahora en 0,46: por encima de medio segundo el intercambio
 * dejaba de leerse como una respuesta a lo que se acababa de pulsar y pasaba
 * a leerse como una espera.
 */
const SWAP_EASING = motion.recorrido;
const SWAP_DURATION = `${timing.intercambio}ms`;

/** Duración compartida con LoginPage para retirar la pantalla ya desplazada. */
export const MOBILE_AUTH_TRANSITION_MS = 820;

const mobilePageDown = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }
  82% {
    transform: translate3d(0, 102%, 0);
    animation-timing-function: cubic-bezier(0.2, 0.75, 0.25, 1);
  }
  91% {
    transform: translate3d(0, 97.5%, 0);
    animation-timing-function: cubic-bezier(0.3, 0, 0.2, 1);
  }
  100% { transform: translate3d(0, calc(100% + 2px), 0); }
`;

const mobilePageUp = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }
  82% {
    transform: translate3d(0, -102%, 0);
    animation-timing-function: cubic-bezier(0.2, 0.75, 0.25, 1);
  }
  91% {
    transform: translate3d(0, -97.5%, 0);
    animation-timing-function: cubic-bezier(0.3, 0, 0.2, 1);
  }
  100% { transform: translate3d(0, calc(-100% - 2px), 0); }
`;

const MOBILE_FORM_BACKGROUND = `linear-gradient(180deg, ${alpha(
  marcaFija.violet,
  0.18,
)} 0%, ${alpha(marcaFija.violet, 0.08)} 34%, ${alpha(
  marcaFija.violet,
  0.02,
)} 62%, transparent 82%)`;

const CLIP_ON_LEFT = "polygon(0 0, 100% 0, 88% 100%, 0 100%)";
const CLIP_ON_RIGHT = "polygon(12% 0, 100% 0, 100% 100%, 0 100%)";

/**
 * Marco de las pantallas de acceso.
 *
 * A partir de 621 px los dos paneles ocupan media pantalla cada uno y, al
 * pasar a registro, **intercambian su lado** deslizándose (`translateX(±100%)`).
 *
 * El panel de marca se apila por encima (`zIndex` 2) y el del formulario por
 * debajo (`zIndex` 1): así, durante el cruce, la tarjeta pasa POR DETRÁS del
 * bloque del claim en lugar de montársele encima.
 *
 * Cuando se pasa `switchAction`, el panel lleva la invitación a la otra
 * pantalla; si no, muestra los puntos de confianza.
 */
export function AuthShell({
  title,
  titleAccent,
  subtitle,
  trustPoints = [],
  swapped = false,
  switchQuestion,
  switchAction,
  switchBack = false,
  onSwitch,
  mobileLeavingContent,
  mobileLeavingSwapped = false,
  mobileLeavingDirection = "down",
  onMobileLeavingEnd,
  children,
}) {
  return (
    <Box
      className="light adl-atlas-auth"
      sx={{
        /*
          El acceso va siempre en claro.

          Es la única pantalla construida sobre una ilustración de marca —la
          hoja blanca, el degradado violeta, el escaneo—, y esa escena está
          dibujada con colores fijos: en oscuro convivía un panel de marca
          claro con un formulario de noche al lado, que no es un tema, es una
          costura. Dentro ya se respeta lo que cada uno tenga puesto.

          `className="light"` engancha con el selector de esquema de MUI, que
          publica sus variables en `:root, .light`. Las de marca se declaran
          en la raíz, así que hay que traerlas a mano; `colorScheme` le dice
          además al navegador de qué color pintar lo suyo —campos nativos,
          desplegables del sistema— dentro de este subárbol.
        */
        ...variablesClaras,
        ...authFieldsSx,
        colorScheme: "light",
        /*
          Y se vuelve a fijar el color del texto.

          Redefinir las variables no basta: `color` es una propiedad heredada
          y `<body>` —que queda fuera de este subárbol— ya la resolvió con la
          tinta del tema en curso. Todo lo que no declara color propio la
          hereda ya calculada, así que con el sistema en oscuro el titular
          «Accede a tu portal» salía en blanco roto sobre fondo blanco.
          Medido: `rgb(231, 237, 243)` mientras la variable de aquí dentro
          decía `#172b3d`.
        */
        color: "text.primary",
        minHeight: ALTO_PANTALLA,
        bgcolor: "background.paper",
        // Referencia del selector de idioma, que va anclado a la esquina de
        // la pantalla y no a ninguno de los dos paneles.
        position: "relative",
        [SPLIT]: { overflow: "hidden" },
      }}
    >
      <Box
        className="adl-auth-brand-panel"
        sx={{
          position: "relative",
          zIndex: 2,
          display: "none",
          flexDirection: "column",
          justifyContent: "center",
          bgcolor: marcaFija.violet,
          color: "#fff",
          overflow: "hidden",
          [SPLIT]: {
            display: "flex",
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: "50%",
            transform: swapped ? "translateX(100%)" : "none",
            // El borde interior va en diagonal y cambia de lado con el panel:
            // la inclinación siempre mira hacia el formulario. Cambia de golpe
            // a mitad del recorrido en lugar de interpolarse: animar un
            // `polygon()` obliga a repintar medio viewport en cada fotograma,
            // y el resultado no se distingue de este.
            clipPath: swapped ? CLIP_ON_RIGHT : CLIP_ON_LEFT,
            transition: `transform ${SWAP_DURATION} ${SWAP_EASING}, clip-path 0s linear ${timing.intercambio / 2}ms`,
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            [SPLIT]: { transition: "none" },
          },
        }}
      >
        <Box aria-hidden="true" className="adl-auth-photo" sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <Box component="img" src="/brand/atlas-comercial-presupuestos-v1.webp" alt="" fetchPriority="high"
            sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "60% 32%", opacity: .65, mixBlendMode: "luminosity" }} />
          <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, rgba(70,58,134,.25) 0%, rgba(70,58,134,.65) 48%, rgba(44,36,85,.96) 100%)" }} />
        </Box>
        <AdaltraTubes />

        {/*
          El contenido se desplaza dentro de su propia caja, no dentro del
          panel.

          Los anillos de arriba están posicionados en absoluto y asoman 200 px
          por debajo del borde: si el desplazamiento viviera en el panel, esos
          200 px contarían como alto desplazable y saldría una barra
          permanente aunque el contenido cupiese de sobra. Dejándolos fuera de
          la caja que se desplaza, la barra aparece solo cuando de verdad
          falta sitio.

          `safe center` es lo que evita que un contenido más alto que la
          ventana se recorte por ARRIBA y quede inalcanzable: en ese caso el
          bloque se apoya arriba y se puede recorrer entero. Con `center` a
          secas, una ventana de portátil a media altura dejaba el logotipo
          cortado y el botón de crear cuenta por debajo del borde.
        */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "safe center",
            // El bloque se centra también en horizontal.
            //
            // Con el acolchado en porcentaje y un ancho máximo de 480, en una
            // pantalla ancha el contenido se quedaba pegado al borde izquierdo
            // del panel y dejaba cientos de píxeles vacíos a la derecha: se
            // leía como descolgado, no como compuesto. El texto sigue alineado
            // a la izquierda dentro del bloque —una columna de lectura no se
            // centra—, lo que se centra es la columna entera.
            alignItems: "safe center",
            minHeight: 0,
            flex: 1,
            overflowY: "auto",
            // Cuando hace falta barra, se reserva su hueco a los DOS lados.
            // Si no, la barra se come diez píxeles solo por la derecha y el
            // bloque centrado deja de estarlo justo en las ventanas bajas,
            // que son las que la sacan.
            scrollbarGutter: "stable both-edges",
            overscrollBehavior: "contain",
            /**
             * El bloque no va al centro de la caja, va al centro de lo que se
             * VE.
             *
             * El panel está recortado en diagonal por el lado que da al
             * formulario, así que la mitad geométrica y la mitad visible no
             * coinciden: centrado sobre la caja, el contenido se lee corrido
             * hacia el borde inclinado. Se compensa con la mitad de la
             * inclinación (12 % / 2) de acolchado extra por ese lado.
             *
             * Y sigue a la diagonal, no a un lado fijo: al pasar a registro el
             * panel cruza y el recorte se va al otro borde. Un desplazamiento
             * siempre a la izquierda dejaría aquí el logotipo metido dentro
             * del recorte, es decir, cortado.
             */
            pl: swapped ? "15%" : "9%",
            pr: swapped ? "9%" : "15%",
            py: 7,
          }}
        >
          <Box sx={{ position: "relative", width: "100%", maxWidth: 480 }}>
            {/* El logotipo hace de «A» de Atlas, igual que en el acceso de
                móvil: el símbolo YA es una A, así que ponerlo al lado de la
                palabra la escribía dos veces. Aquí comparte eje izquierdo con
                el titular en lugar de ir centrado. */}
            <Brand hero onDark lettermark align="flex-start" />
            <Box sx={{ height: "clamp(24px, 4vh, 40px)" }} />

            <Typography variant="h1" sx={{ color: "#fff", mb: 1.5, fontWeight: 800, fontSize: "clamp(26px, 2.5vw, 44px)", lineHeight: 1.16 }}>
              <AnimatedTitle text={title} accent={titleAccent} brandMarks />
            </Typography>
            <Typography
              className="adl-auth-subtitle" key={subtitle}
              variant="body1"
              sx={{ color: "rgba(255,255,255,0.82)", mb: 4 }}
            >
              {subtitle}
            </Typography>

            {/* La invitación a la otra pantalla vive en el panel que se mueve:
              así el desplazamiento tiene sentido — se aparta justamente la
              opción que no has elegido. Sustituye a los puntos de confianza
              para no recargar la columna. */}
            {switchAction ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.82)", mb: 1.5 }}
                >
                  {switchQuestion}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={onSwitch}
                  startIcon={switchBack ? <ChevronLeftIcon /> : null}
                  endIcon={switchBack ? null : <ChevronRightIcon />}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.55)",
                    px: 3,
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  {switchAction}
                </Button>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {trustPoints.map((point, index) => (
                  <Stack
                    key={point}
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(255,255,255,0.14)",
                        flexShrink: 0,
                      }}
                    >
                      {index === 0 ? (
                        <GppGoodIcon fontSize="small" />
                      ) : (
                        <FactCheckIcon fontSize="small" />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      {point}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          // Velo del violeta corporativo que baja hasta disolverse a la
          // altura de los campos, para que el formulario quede sobre fondo
          // limpio. Al 9 % daba #EEEDF5 y no se distinguía del blanco en un
          // móvil; al 18 % (#DEDCE9) ya se lee como degradado sin llegar a
          // competir con el contenido. En pantalla ancha se quita: el color lo
          // pone el panel de marca.
          //
          // El degradado muere en `transparent` y no en blanco: lo que hay
          // debajo es el fondo del tema, y rematarlo en blanco dejaba media
          // pantalla clavada en claro con el texto ya en tonos de noche
          // encima. El velo se mantiene en violeta fijo porque es la marca,
          // y sobre fondo oscuro se lee igual de bien.
          bgcolor: "background.paper",
          backgroundImage: MOBILE_FORM_BACKGROUND,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          // En móvil el contenido se apoya arriba con un margen proporcional a
          // la pantalla, no centrado: centrado y con la marca a este cuerpo
          // quedaba pegado al borde superior. El `clamp` lo hace ceder en
          // pantallas cortas, donde ese aire no sobra.
          justifyContent: "flex-start",
          px: { xs: 2.5, sm: 4 },
          // Arriba y abajo por separado: con el atajo `py` no hay forma de dar
          // más aire arriba que abajo.
          pt: swapped
            ? "clamp(16px, 4vh, 40px)"
            : "clamp(24px, 9vh, 92px)",
          pb: { xs: swapped ? 1.5 : 2.5, sm: 6 },
          minHeight: ALTO_PANTALLA,
          ...(mobileLeavingContent && {
            "@media (max-width:620px)": {
              "& > form": { animation: "none" },
            },
          }),
          [SPLIT]: {
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: "50%",
            overflowY: "auto",
            minHeight: "auto",
            // En pantalla ancha el formulario comparte fila con el panel de
            // marca y ahí sí va centrado verticalmente.
            justifyContent: "center",
            // El color lo pone el panel de marca de al lado; aquí estorbaría.
            backgroundImage: "none",
            px: "clamp(32px, 5vw, 78px)",
            py: 9,
            pt: 9,
            transform: swapped ? "translateX(-100%)" : "none",
            transition: `transform ${SWAP_DURATION} ${SWAP_EASING}`,
          },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <Box
          sx={{
            display: "block",
            mb: swapped ? 1.25 : 2,
            [SPLIT]: { display: "none" },
          }}
        >
          <Brand
            hero
            lettermark
            esquema="claro"
            colorTexto="marca"
            scale={swapped ? 0.82 : 1}
          />
        </Box>

        {children}
      </Box>

      {/*
        En móvil se desplaza una pantalla completa, no una tarjeta. La copia
        saliente incluye el fondo, la marca, el formulario y el idioma; la
        pantalla nueva ya está renderizada debajo y se descubre al mover este
        plano. En escritorio sigue vigente el cruce lateral de paneles.
      */}
      {mobileLeavingContent && (
        <Box
          aria-hidden
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget) onMobileLeavingEnd?.();
          }}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 5,
            minHeight: ALTO_PANTALLA,
            overflowY: "auto",
            bgcolor: "background.paper",
            backgroundImage: MOBILE_FORM_BACKGROUND,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            px: { xs: 2.5, sm: 4 },
            pt: mobileLeavingSwapped
              ? "clamp(16px, 4vh, 40px)"
              : "clamp(24px, 9vh, 92px)",
            pb: { xs: mobileLeavingSwapped ? 1.5 : 2.5, sm: 6 },
            pointerEvents: "none",
            willChange: "transform",
            backfaceVisibility: "hidden",
            contain: "paint",
            animation: `${
              mobileLeavingDirection === "up" ? mobilePageUp : mobilePageDown
            } ${MOBILE_AUTH_TRANSITION_MS}ms linear both`,
            "& form": { animation: "none !important" },
            [SPLIT]: { display: "none" },
            "@media (prefers-reduced-motion: reduce)": { display: "none" },
          }}
        >
          <Box sx={{ mb: mobileLeavingSwapped ? 1.25 : 2 }}>
            <Brand
              hero
              lettermark
              esquema="claro"
              colorTexto="marca"
              scale={mobileLeavingSwapped ? 0.82 : 1}
            />
          </Box>

          {mobileLeavingContent}

          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              borderRadius: "10px",
              bgcolor: "background.paper",
              boxShadow: "0 2px 10px rgba(16, 47, 76, 0.10)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <LanguageMenu />
          </Box>
        </Box>
      )}

      {/*
        El idioma vive fuera de los dos paneles.

        Antes estaba dentro del panel del formulario, así que al pasar a
        registro cruzaba la pantalla entera montado en él. Un control que no
        pertenece al contenido no debería viajar con el contenido: se queda
        quieto arriba a la derecha, y lleva fondo propio porque debajo puede
        haber tanto el blanco del formulario como el violeta de la marca.
      */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 4,
          borderRadius: "10px",
          bgcolor: "background.paper",
          boxShadow: "0 2px 10px rgba(16, 47, 76, 0.10)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <LanguageMenu />
      </Box>
    </Box>
  );
}
